/**
 * Iter-45: Marketing pixel injection + Lead conversion event firing.
 *
 * Called from CustomLandingPage.jsx (PageView on mount) and ThankYou.jsx
 * (Lead conversion on submit). Idempotent — each provider's base script
 * is loaded at most once per document via a `data-pixel` marker.
 *
 * All providers are optional. Missing ids are silently skipped.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;
const MARK = "data-lp-pixel";

/** Inject a script tag once, keyed by a stable id. */
function injectScript(id, factory) {
  if (typeof document === "undefined") return;
  if (document.querySelector(`script[${MARK}="${id}"]`)) return;
  try {
    const script = document.createElement("script");
    script.setAttribute(MARK, id);
    script.async = true;
    factory(script);
    document.head.appendChild(script);
  } catch (err) {
    // Never surface tracking errors to the consumer.
    if (typeof console !== "undefined" && console.warn) {
      console.warn(`[pixel] failed to inject ${id}`, err);
    }
  }
}

/** Inject a raw <script> block once, keyed by id. */
function injectInline(id, code) {
  if (typeof document === "undefined") return;
  if (document.querySelector(`script[${MARK}="${id}"]`)) return;
  try {
    const script = document.createElement("script");
    script.setAttribute(MARK, id);
    script.text = code;
    document.head.appendChild(script);
  } catch (err) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn(`[pixel] failed to inject ${id}`, err);
    }
  }
}

/** Load Meta Pixel base once, then init + PageView per pixel id. */
export function injectMetaPixel(pixelId) {
  if (!pixelId) return;
  injectInline(
    "meta-base",
    `!function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');`,
  );
  injectInline(
    `meta-init-${pixelId}`,
    `fbq('init', '${pixelId}');
      fbq('trackSingle', '${pixelId}', 'PageView');`,
  );
}

/** Load gtag.js base for Google Ads / GA4 and fire config. */
export function injectGoogleTag(gaOrAdsId) {
  if (!gaOrAdsId) return;
  injectScript("gtag-loader", (s) => {
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaOrAdsId)}`;
  });
  injectInline(
    "gtag-init",
    `window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    window.gtag = window.gtag || gtag;
    gtag('js', new Date());`,
  );
  injectInline(
    `gtag-config-${gaOrAdsId}`,
    `if (window.gtag) window.gtag('config', '${gaOrAdsId}');`,
  );
}

/** Load TikTok Pixel stub once, then load + page per pixel id. */
export function injectTikTokPixel(pixelId) {
  if (!pixelId) return;
  injectInline(
    "tiktok-base",
    `!function (w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
    }(window, document, 'ttq');`,
  );
  injectInline(
    `tiktok-init-${pixelId}`,
    `window.ttq.load('${pixelId}');
      window.ttq.instance('${pixelId}').page();`,
  );
}

/**
 * Inject every configured pixel and fire the page-view event for each.
 *
 * `cfg`: { meta_pixel_id, google_ads_id, ga4_measurement_id, tiktok_pixel_id, google_ads_conversion_label }
 */
export function injectAllPixels(cfg) {
  if (!cfg || typeof cfg !== "object") return;
  injectMetaPixel(cfg.meta_pixel_id);
  // Google Ads and GA4 both share gtag.js; load the base once per configured id.
  injectGoogleTag(cfg.google_ads_id);
  injectGoogleTag(cfg.ga4_measurement_id);
  injectTikTokPixel(cfg.tiktok_pixel_id);
}

/**
 * Fire the standard "Lead conversion" event on every configured provider.
 * Called on the ThankYou page (or immediately after a successful form POST).
 *
 * `cfg`: same shape as injectAllPixels.
 * `context`: optional { value, currency, reference } for extra event props.
 */
export function fireLeadConversion(cfg, context = {}) {
  if (!cfg || typeof cfg !== "object") return;
  const { value, currency = "USD", reference } = context;

  // Meta — standard Lead event, targeted so multiple pixels don't double-count.
  try {
    if (cfg.meta_pixel_id && typeof window.fbq === "function") {
      window.fbq("trackSingle", cfg.meta_pixel_id, "Lead", {
        currency,
        ...(value != null ? { value } : {}),
        ...(reference ? { content_name: reference } : {}),
      });
    }
  } catch (err) { /* swallow */ }

  // Google Ads — conversion event (needs both id + label).
  try {
    if (cfg.google_ads_id && cfg.google_ads_conversion_label
         && typeof window.gtag === "function") {
      window.gtag("event", "conversion", {
        send_to: `${cfg.google_ads_id}/${cfg.google_ads_conversion_label}`,
        ...(value != null ? { value, currency } : {}),
        ...(reference ? { transaction_id: reference } : {}),
      });
    }
  } catch (err) { /* swallow */ }

  // GA4 — generate_lead event.
  try {
    if (cfg.ga4_measurement_id && typeof window.gtag === "function") {
      window.gtag("event", "generate_lead", {
        send_to: cfg.ga4_measurement_id,
        currency,
        ...(value != null ? { value } : {}),
        ...(reference ? { transaction_id: reference } : {}),
      });
    }
  } catch (err) { /* swallow */ }

  // TikTok — SubmitForm event.
  try {
    if (cfg.tiktok_pixel_id && window.ttq && typeof window.ttq.instance === "function") {
      window.ttq.instance(cfg.tiktok_pixel_id).track("SubmitForm", {
        ...(value != null ? { value, currency } : {}),
        ...(reference ? { content_id: reference } : {}),
      });
    }
  } catch (err) { /* swallow */ }
}


// ---------------------------------------------------------------------------
// Iter-45.2: Reusable hook for the six hardcoded verticals + PI split
// router. Fetches the platform-wide pixel config from /api/public/pixels
// and injects the base scripts on mount. Skipped in preview mode so admin
// previews don't pollute ad platforms with fake PageView events.
//
// Custom buyer LPs do NOT use this hook — their pixel config is bundled
// into the public /custom-landing/{slug} response (with per-LP overrides
// merged in) and injected inline by CustomLandingPage.jsx.
// ---------------------------------------------------------------------------

/**
 * Fetch the global pixel config from the public endpoint.
 * Returns null on error so callers can proceed uninstrumented.
 */
export async function fetchPublicPixels() {
  try {
    const { data } = await axios.get(`${API}/public/pixels`);
    return data?.pixels || null;
  } catch (err) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[pixel] failed to fetch public pixels", err);
    }
    return null;
  }
}

/**
 * React hook: fetch global pixels + inject base scripts on mount.
 *
 * @param {object} opts
 * @param {boolean} opts.preview  When true, skip network + injection.
 * @returns {{ pixels: object|null }}  the fetched config, useful if the
 *          caller also needs to fire the Lead conversion event later.
 */
export function usePublicPixels({ preview = false } = {}) {
  const [pixels, setPixels] = useState(null);
  useEffect(() => {
    if (preview) return;
    let cancelled = false;
    fetchPublicPixels().then((cfg) => {
      if (cancelled || !cfg) return;
      setPixels(cfg);
      injectAllPixels(cfg);
    });
    return () => { cancelled = true; };
  }, [preview]);
  return { pixels };
}
